export const isValidContactEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

export const FLIGHT_SEARCH_CONTACT_KEY = "flightSearchContact";
export const USER_DETAILS_PROVIDED_KEY = "userDetailsProvided";
export const PHONE_MODAL_SUPPRESS_UNTIL_KEY = "phoneModalSuppressUntil";
export const PHONE_MODAL_COOKIE = "skysale_phone_modal_until";
export const PHONE_MODAL_SUPPRESS_DAYS = 10;
export const CONTACT_CHANGED_EVENT = "skysale-contact-changed";

const PHONE_MODAL_MS = PHONE_MODAL_SUPPRESS_DAYS * 24 * 60 * 60 * 1000;

export const notifyContactChanged = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(CONTACT_CHANGED_EVENT));
};

export const isValidContactPhone = (phoneNumber) => {
  const digits = (phoneNumber || "").replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

export const hasFlightSearchContactDetails = (booking) => {
  if (!booking) return false;
  const phone = (booking.phone || "").trim();
  return Boolean(phone && isValidContactPhone(phone));
};

const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const writeCookie = (name, value, maxAgeSeconds) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

/** Survives localStorage.clear() — used so the homepage modal stays hidden for 10 days */
export const markPhoneModalSuppressed = (days = PHONE_MODAL_SUPPRESS_DAYS) => {
  if (typeof window === "undefined") return;
  const until = Date.now() + days * 24 * 60 * 60 * 1000;
  localStorage.setItem(PHONE_MODAL_SUPPRESS_UNTIL_KEY, String(until));
  writeCookie(PHONE_MODAL_COOKIE, String(until), days * 24 * 60 * 60);
};

export const isPhoneModalSuppressed = () => {
  if (typeof window === "undefined") return false;

  const candidates = [
    localStorage.getItem(PHONE_MODAL_SUPPRESS_UNTIL_KEY),
    readCookie(PHONE_MODAL_COOKIE),
  ];

  for (const raw of candidates) {
    const until = Number(raw);
    if (!Number.isFinite(until)) continue;
    if (Date.now() <= until) {
      // Keep localStorage in sync if only the cookie survived a clear
      if (!localStorage.getItem(PHONE_MODAL_SUPPRESS_UNTIL_KEY)) {
        localStorage.setItem(PHONE_MODAL_SUPPRESS_UNTIL_KEY, String(until));
      }
      return true;
    }
  }

  localStorage.removeItem(PHONE_MODAL_SUPPRESS_UNTIL_KEY);
  return false;
};

export const snapshotContactStorage = () => {
  if (typeof window === "undefined") return {};
  const keys = [
    FLIGHT_SEARCH_CONTACT_KEY,
    USER_DETAILS_PROVIDED_KEY,
    PHONE_MODAL_SUPPRESS_UNTIL_KEY,
  ];
  const snap = {};
  keys.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value != null) snap[key] = value;
  });
  return snap;
};

export const restoreContactStorage = (snap = {}) => {
  if (typeof window === "undefined") return;
  Object.entries(snap).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
};

export const getSavedFlightSearchContact = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = localStorage.getItem(FLIGHT_SEARCH_CONTACT_KEY);
    if (!raw) return null;
    const contact = JSON.parse(raw);
    if (!hasFlightSearchContactDetails(contact)) return null;

    const savedAt = contact.savedAt != null ? Number(contact.savedAt) : null;
    if (Number.isFinite(savedAt) && Date.now() - savedAt > PHONE_MODAL_MS) {
      localStorage.removeItem(FLIGHT_SEARCH_CONTACT_KEY);
      localStorage.removeItem(USER_DETAILS_PROVIDED_KEY);
      return null;
    }

    return contact;
  } catch {
    return null;
  }
};

export const saveFlightSearchContact = ({ name, email, phone } = {}) => {
  if (typeof window === "undefined") return;

  localStorage.setItem(
    FLIGHT_SEARCH_CONTACT_KEY,
    JSON.stringify({
      name: (name || "Guest").trim() || "Guest",
      email: (email || "").trim(),
      phone: (phone || "").trim(),
      savedAt: Date.now(),
    })
  );
  localStorage.setItem(USER_DETAILS_PROVIDED_KEY, "true");
  markPhoneModalSuppressed(PHONE_MODAL_SUPPRESS_DAYS);
  notifyContactChanged();
};

export const hasProvidedFlightSearchContact = () =>
  Boolean(getSavedFlightSearchContact());

/** Phone modal: hide for 10 days after phone is entered */
export const shouldShowHomepagePhoneModal = () => {
  if (isPhoneModalSuppressed()) return false;
  if (hasProvidedFlightSearchContact()) return false;
  return true;
};

export const getBookingDismissKey = (booking = {}) =>
  `${booking.departure || ""}-${booking.arrival || ""}-${booking.date || ""}-${booking.return_date || ""}`;

export const isContactModalDismissedForBooking = (booking) => {
  if (typeof window === "undefined") return false;
  const dismissKey = getBookingDismissKey(booking);
  if (!dismissKey || dismissKey === "---") return false;
  return sessionStorage.getItem("flightSearchModalDismissed") === dismissKey;
};

export const dismissContactModalForBooking = (booking) => {
  if (typeof window === "undefined") return;
  sessionStorage.setItem("flightSearchModalDismissed", getBookingDismissKey(booking));

  if (!getSavedFlightSearchContact()) {
    localStorage.removeItem(USER_DETAILS_PROVIDED_KEY);
  }
};

export const mergeSavedContactIntoBooking = (booking = {}) => {
  const savedContact = getSavedFlightSearchContact();
  if (!savedContact) return booking;

  return {
    ...booking,
    name: booking.name || savedContact.name || "",
    email: booking.email || savedContact.email || "",
    phone: booking.phone || savedContact.phone,
  };
};

export const getDestinationDisplayName = (booking) => {
  if (!booking) return "your destination";

  const name = (booking.arrival_name || booking.destination_name || "").trim();
  if (name) {
    const city = name.split("(")[0].trim();
    return city || name;
  }

  return (booking.arrival || "your destination").trim();
};

export const buildFlightInquiryPayload = (booking, contact) => ({
  full_name: contact.name || contact.full_name || "Guest",
  email: contact.email || null,
  phone: contact.phone,
  departure: booking.departure,
  arrival: booking.arrival,
  departure_name: booking.departure_name,
  arrival_name: booking.arrival_name,
  date: booking.date,
  return_date: booking.return_date || "",
  adults: booking.adults || "1",
  children: booking.children || "0",
  infants: booking.infants || "0",
  flight_type: booking.flight_type || "one-way",
  flight_class: booking.flight_class || "economy",
  description: `${booking.flight_type === "return" ? "Round Trip" : "One Way"} - ${booking.flight_class || "economy"}`,
  status: "SEARCH",
});

/**
 * Create a guest lead (phone only, no route) for the admin Guests panel.
 */
export const submitGuestLead = async ({ phone, name = "Guest", email = "" } = {}) => {
  const contactPhone = (phone || "").trim();
  if (!contactPhone || !isValidContactPhone(contactPhone)) return null;

  try {
    const service = (await import("../Service/Service")).default;
    const result = await service.createFlightInquiry({
      full_name: name || "Guest",
      email: email || null,
      phone: contactPhone,
      lead_type: "homepage_guest",
      status: "GUEST",
      description: "Homepage guest lead",
    });
    return result?.data || result;
  } catch (error) {
    console.error("Error creating guest lead:", error);
    return null;
  }
};

/**
 * Create an Available Inquiry once we have phone + origin/destination.
 * Also upgrades an existing guest lead to a route inquiry on the server.
 */
export const submitRouteInquiryIfReady = async (bookingLike = {}, extras = {}) => {
  const booking = mergeSavedContactIntoBooking(bookingLike);
  const phone = (booking.phone || "").trim();
  const origin = booking.departure || booking.origin || booking.origin_code;
  const destination = booking.arrival || booking.destination || booking.destination_code;

  if (!phone || !isValidContactPhone(phone)) return null;
  if (!origin || !destination) return null;

  const contact = {
    name: booking.name || "Guest",
    email: booking.email || null,
    phone,
  };

  const payload = {
    ...buildFlightInquiryPayload(
      {
        ...booking,
        departure: origin,
        arrival: destination,
        departure_name:
          booking.departure_name ||
          booking.origin_city ||
          extras.departure_name ||
          origin,
        arrival_name:
          booking.arrival_name ||
          booking.destination_city ||
          extras.arrival_name ||
          destination,
      },
      contact
    ),
    ...extras,
  };

  try {
    // Lazy import to avoid circular deps at module load
    const service = (await import("../Service/Service")).default;
    const result = await service.createFlightInquiry(payload);
    return result?.data || result;
  } catch (error) {
    console.error("Error creating route inquiry:", error);
    return null;
  }
};
